import { gql, useQuery } from '@apollo/client';
import ProductionByMonthStackedByTypeChartThumbnail from '@assets/images/report-components/production-by-month-stacked-by-type-chart-thumbnail.png';
import { type Theme, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import Graph from '@/components/Graphs/graph';
import type { AssetAggregatesStacked } from '@/types/GraphqlSchema';
import { createDateWithYearOffset } from '@/utils/dateFormatter';
import { getNestedValue } from '@/utils/helpers';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'production_by_month_stacked_by_type_chart';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        values
      }
      stacks
    }
  }
`;

enum ProductionType {
  Developed = 'pdp',
  Undeveloped = 'pud',
}

enum ProductionPortfolio {
  PDP_OIL = 'Oil PDP',
  PDP_GAS = 'Gas PDP',
  PDP_CONDENSATE = 'Condensate PDP',
  PUD_OIL = 'Oil UD',
  PUD_GAS = 'Gas UD',
  PUD_CONDENSATE = 'Condensate UD',
}

const PRODUCTION_TYPE_MAP = {
  pdp_oil: {
    name: ProductionPortfolio.PDP_OIL,
    type: ProductionType.Developed,
    field: 'oilProductionsSum',
  },
  pdp_gas: {
    name: ProductionPortfolio.PDP_GAS,
    type: ProductionType.Developed,
    field: 'gasProductionsBoedSum',
  },
  pdp_condensate: {
    name: ProductionPortfolio.PDP_CONDENSATE,
    type: ProductionType.Developed,
    field: 'condensateProductionsSum',
  },
  pud_oil: {
    name: ProductionPortfolio.PUD_OIL,
    type: ProductionType.Undeveloped,
    field: 'oilProductionsSum',
  },
  pud_gas: {
    name: ProductionPortfolio.PUD_GAS,
    type: ProductionType.Undeveloped,
    field: 'gasProductionsBoedSum',
  },
  pud_condensate: {
    name: ProductionPortfolio.PUD_CONDENSATE,
    type: ProductionType.Undeveloped,
    field: 'condensateProductionsSum',
  },
};

const COLOR_MAP = {
  [ProductionPortfolio.PDP_OIL]: 'palette.chartColors.earthGreen.c100',
  [ProductionPortfolio.PDP_GAS]: 'palette.chartColors.trueRed.c100',
  [ProductionPortfolio.PDP_CONDENSATE]: 'palette.chartColors.amber.c100',
  [ProductionPortfolio.PUD_OIL]: 'palette.chartColors.aqua.c100',
  [ProductionPortfolio.PUD_GAS]: 'palette.chartColors.trueRed.c50',
  [ProductionPortfolio.PUD_CONDENSATE]: 'palette.chartColors.amber.c50',
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(QUERY, {
    variables: {
      input: {
        by: 'productionMonth',
        fields: ['oilProductionsSum', 'gasProductionsBoedSum', 'condensateProductionsSum'],
        stackBy: 'productionType',
        portfolio: {
          ...portfolio,
          productionTypes: Object.values(ProductionType),
          startDate: createDateWithYearOffset(-50),
          endDate: createDateWithYearOffset(50),
        },
      },
    },
    skip: !!persistedData,
    onCompleted: response => {
      if (response?.assetAggregatesStacked) {
        const chartData = response.assetAggregatesStacked;
        setChartDataFetched(chartData);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: chartData,
          });
        }
      }
    },
  });

  return { chartData: chartDataFetched, error, loading };
};

type TickData = {
  tickVals: string[];
  tickText: string[];
};

const extractXAxisData = (data: AssetAggregatesStacked): string[] => {
  return data.data?.map(item => item.group) || [];
};

const generateXAxisTicks = (xAxisData: string[]): TickData => {
  const tickVals: string[] = [];
  const tickText: string[] = [];

  xAxisData.forEach((dateStr, index) => {
    if (index % 36 === 0) {
      // Every 36 months (3 years)
      tickVals.push(dateStr);
      const year = new Date(dateStr).getFullYear();
      tickText.push(year.toString());
    }
  });

  return { tickVals, tickText };
};

const formatChartData = (data: AssetAggregatesStacked, theme: Theme) => {
  try {
    const { data: chartData } = data;

    return Object.values(PRODUCTION_TYPE_MAP).map((stack, index) => {
      const x = chartData.map(item => item.group);
      const y = chartData.map(item => {
        const stackValues = item.values[stack.type];
        return stackValues?.[stack.field] || 0;
      });

      const color = getNestedValue(theme, COLOR_MAP[stack.name]);

      return {
        x,
        y,
        type: 'scatter',
        mode: 'lines',
        name: stack.name,
        stackgroup: 'one',
        fillcolor: color,
        line: { color, width: 0 },
        legendrank: Object.values(PRODUCTION_TYPE_MAP).length - index,
      };
    });
  } catch {
    return null;
  }
};

const calculateMaxYForStackedData = (data: AssetAggregatesStacked) => {
  try {
    const { data: chartData } = data;

    // Calculate the cumulative maximum by summing all production types for each time period
    const cumulativeMaxY = chartData.reduce((maxSoFar, item) => {
      const totalForThisPeriod = Object.values(PRODUCTION_TYPE_MAP).reduce((sum, stack) => {
        const stackValues = item.values[stack.type];
        const value = stackValues?.[stack.field] || 0;
        return sum + value;
      }, 0);

      return Math.max(maxSoFar, totalForThisPeriod);
    }, 0);

    return cumulativeMaxY;
  } catch {
    return undefined;
  }
};

const ComponentView = ({ data }: ComponentViewProps<AssetAggregatesStacked>) => {
  const theme = useTheme();
  const formattedData = useMemo(() => formatChartData(data, theme), [data, theme]);
  const maxY = useMemo(() => calculateMaxYForStackedData(data), [data]);

  const { tickVals, tickText } = useMemo(() => {
    const xAxisData = extractXAxisData(data);
    return generateXAxisTicks(xAxisData);
  }, [data]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Production Profile</ComponentTitle>
      <Graph
        data={formattedData}
        layout={getLayout({
          xaxis: { tickvals: tickVals, ticktext: tickText },
          yaxis: {
            title: { text: 'boe/d' },
            ...(maxY ? { range: [0, maxY * 1.1] } : {}),
          },
          legend: { y: -0.3 },
        })}
        title={undefined}
        config={undefined}
        xlabel={undefined}
        ylabel={undefined}
        loading={undefined}
        onUpdate={undefined}
        onClick={undefined}
        onRelayout={undefined}
        onInitialized={undefined}
        onAfterPlot={undefined}
        maxY={undefined}
        darkTheme={undefined}
        context={undefined}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Production Profile
    </ComponentTitle>
    <img
      src={ProductionByMonthStackedByTypeChartThumbnail}
      alt='Production Profile Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const ProductionByMonthStackedByTypeChartComponent: ComponentDefinition<AssetAggregatesStacked> =
  {
    id: COMPONENT_ID,
    name: 'Production Profile',
    type: SmartReportComponentTypesEnum.Chart,
    reportTypes: [SmartReportTypeEnum.Asset],
    Thumbnail: ComponentThumbnail,
    Component: ComponentEdit,
    ComponentView,
  };
